// CEFR readability — a concrete, audience-relative reading of the LIX score.
//
// The linguistic axis computes a LIX score (Björnsson's Läsbarhetsindex). On its
// own a LIX band is an ABSOLUTE, audience-blind word ("hard" / "svår") — it can't
// answer "hard for whom?". CEFR (A1–C2), the Common European Framework reading
// scale most Nordic higher-ed and L&D audiences already speak, gives a concrete
// anchor: instead of "hard" the panel reads "~B2" — a level an author can compare
// against their own audience.
//
// The mapping LIX → CEFR is APPROXIMATE. LIX measures sentence length + long-word
// share; CEFR measures a reader's competence — different axes that only correlate.
// The band boundaries below are grounded in Björnsson's original interpretation
// (<30 very easy … >60 very difficult) laid over the CEFR reading levels. The UI
// ALWAYS presents the result softly ("~B2", "ungefär B2") and says in a HelpHint
// that it is an estimate — never a precise claim.
//
// Approximate mapping table (LIX score → CEFR reading level):
//
//     LIX  < 30 : A2   — very easy (children's books, very simple prose)
//     30 ≤ … < 40: B1   — easy (mainstream fiction, simple non-fiction)
//     40 ≤ … < 50: B2   — medium (daily newspapers, general non-fiction)
//     50 ≤ … < 60: C1   — difficult (expository / specialist non-fiction)
//     LIX ≥ 60  : C2   — very difficult (technical / academic text)
//
// A1 is intentionally never produced: A1 reading is a handful of memorised
// phrases, below the floor of any connected prose LIX meaningfully scores. It
// stays in `CefrBand` for completeness (a target audience CAN be A1) so the
// relative comparison below is total.

export type { CefrBand, CefrComparison };
export { compareCefr, lixToCefr };

// The six CEFR levels, easiest → hardest. A closed union: a new level would have
// to be added here and would break `cefrRank`'s exhaustive map until ranked.
type CefrBand = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

// LIX score → approximate CEFR reading level. Pure; boundaries per the table above.
const lixToCefr = (lix: number): CefrBand => {
    if (lix < 30) return "A2";
    if (lix < 40) return "B1";
    if (lix < 50) return "B2";
    if (lix < 60) return "C1";
    return "C2";
};

// A total ordinal for the six bands — the single source the comparison reads from.
// A Record forces a rank for every band, so a new band breaks compilation here.
const cefrRank = (band: CefrBand): number => {
    const ranks: Record<CefrBand, number> = {
        A1: 0,
        A2: 1,
        B1: 2,
        B2: 3,
        C1: 4,
        C2: 5,
    };
    return ranks[band];
};

// How a text's estimated level sits against a target audience level:
//   - `under`  — the text is EASIER than the audience's target (below target).
//   - `at`     — the text sits at the target level.
//   - `over`   — the text is HARDER than the audience's target (above target).
//
// This is the pure half of the audience-relative reading. The RENDER of it waits
// on the audience's target level reaching the quality view: `PolicyEvaluation`
// does not carry it today, so the panel shows the concrete band alone and this
// comparison is unused until the target level is plumbed (a flagged follow-up).
type CefrComparison = "under" | "at" | "over";

const compareCefr = (text: CefrBand, target: CefrBand): CefrComparison => {
    const delta = cefrRank(text) - cefrRank(target);
    if (delta < 0) return "under";
    if (delta > 0) return "over";
    return "at";
};
